import React, { Component, PropTypes } from 'react';
import { Modal, Button, Table } from 'react-fast-ui';
import TourneyTeamSelect from '../inputs/TourneyTeamSelect';
import EmptyState from '../../common/components/EmptyState';
import { EventScheduled, EventLocalDate } from '../event';
import { teamNameWithDivision } from '../../filters/TeamFilters';
import _ from 'underscore';
import { NOTIFICATION_CONTEXT_TYPES } from '../../NotificationSystemProvider';

const GamesTable = ({ teamOne, teamTwo, games }) => {
  return (
    <Table>
      <Table.thead>
          <Table.th>Date</Table.th>
          <Table.th>Time</Table.th>
          <Table.th>Venue</Table.th>
          <Table.th>Court</Table.th>
          <Table.th>Team 1</Table.th>
          <Table.th>Team 2</Table.th>
      </Table.thead>
      <Table.tbody>
        {
          games.map((game) => {
            const { court, homeTeam, visitorTeam } = game;

            const teamDisplay = (team) => {
              if (!team) {
                return '';
              }

              const isOne = team.id === teamOne.id;
              const isTwo = team.id === teamTwo.id;

              if (isOne || isTwo) {
                return <span
                  className={`bg-color-${isOne ? 'warning' : 'success'}`}>{teamNameWithDivision(team)}</span>;
              } else {
                return teamNameWithDivision(team);
              }
            };

            return (
              <Table.tr key={game.id}>
                  <Table.td>
                    <EventLocalDate event={game}/>
                  </Table.td>
                  <Table.td>
                    <EventScheduled event={game}/>
                  </Table.td>
                  <Table.td>
                    {court && court.venue ? court.venue.name : ''}
                  </Table.td>
                  <Table.td>
                    {court ? court.name : ''}
                  </Table.td>
                  <Table.td>
                    {teamDisplay(homeTeam)}
                  </Table.td>
                  <Table.td>
                    {teamDisplay(visitorTeam)}
                  </Table.td>
              </Table.tr>
            );
          })
        }
      </Table.tbody>
    </Table>
  );
};


export default class SwapTeamsModal extends Component {
  static contextTypes = {
    dao: PropTypes.object.isRequired,
    ...NOTIFICATION_CONTEXT_TYPES
  };

  static propTypes = {
    open: PropTypes.bool.isRequired,
    tourneyId: PropTypes.string.isRequired,
    onSwapCompleted: PropTypes.func
  };

  static defaultProps = {
    onSwapCompleted: null
  };

  state = {
    teams: [],
    loadingTeams: null,

    // selected teams
    teamOne: null,
    teamTwo: null,

    // games for teams
    games: null,
    loadingGames: null,

    swapping: null
  };


  componentWillUpdate(nextProps) {
    if (nextProps.open && !this.props.open) {
      this.setState({
        teamOne: null,
        teamTwo: null
      });

      this.loadTeams();
    }
  }

  loadTeams() {
    const { loadingTeams } = this.state;
    const { tourneyId } = this.props;
    const { onError, dao } = this.context;

    if (loadingTeams) {
      loadingTeams.cancel();
    }

    if (!tourneyId) {
      return;
    }

    this.setState({
      loadingTeams: dao.tourneyTeams.getAll({ tourneyId })
        .then(teams => {
          const { teamOne, teamTwo } = this.state;

          this.setState({
            teams,
            loadingTeams: null,
              // we have to do this so we have the most up-to-date team information in the dropdown
            teamOne: teamOne ? _.find(teams, { id: teamOne.id }) : null,
            teamTwo: teamTwo ? _.find(teams, { id: teamTwo.id }) : null
          });
        }
        )
        .catch(onError)
    });
  }

  renderPreview() {
    const { teamOne, teamTwo, games, loadingGames } = this.state;

    if (teamOne === null || teamTwo === null) {
      return <EmptyState>You must first select two teams to swap</EmptyState>;
    }

    if (!games) {
      if (loadingGames) {
        return EmptyState.LOADING;
      } else {
        return <EmptyState>Failed to load game data</EmptyState>;
      }
    }

    if (games.length === 0) {
      return <EmptyState>These teams have no games</EmptyState>;
    }

    return <GamesTable {...{ teamOne, teamTwo, games }}/>;
  }

  loadGames = () => {
    const { loadingGames: pending, teamOne, teamTwo } = this.state;
    const { dao, onError } = this.context;

    if (pending) {
      pending.cancel();
    }

    let loadingGames;

    if (teamOne === null || teamTwo === null) {
      loadingGames = null;
    } else {
      loadingGames = dao.games.getAll({ tourneyTeamId: [ teamOne.id, teamTwo.id ], sort: [ 'A|scheduled' ] })
        .then(games => this.setState({ games }))
        .catch(onError)
        .finally(
          () => this.setState({ loadingGames: null })
        );
    }

    this.setState({ games: null, loadingGames });
  };

  renderBody() {
    const { teams } = this.state;

    const renderSelector = (attributeName) => {
      return (
        <TourneyTeamSelect
          multi={false}
          teams={teams}
          value={this.state[ attributeName ]}
          onChange={(team) => this.setState({ [ attributeName ]: team }, this.loadGames)}
        />
      );
    };

    return (
      <div>
        <div className="display-flex">
          <div className="flex-grow-1 mg-right-sm">
            <label className="bg-color-warning">Team One</label>
            {renderSelector('teamOne')}
          </div>
          <div className="flex-grow-1">
            <label className="bg-color-success">Team Two</label>
            {renderSelector('teamTwo')}
          </div>
        </div>
        <hr />
        { this.renderPreview() }
      </div>
    );
  }

  swapTeams() {
    const { swapping, teamOne, teamTwo } = this.state;
    const { dao, onError, onSuccess } = this.context;

    if (swapping) {
      return;
    }

    this.setState({
      swapping: dao.swapTeams({ teamOne, teamTwo })
        .then(
          () => {
            onSuccess(`Successfully swapped ${teamOne.name} for ${teamTwo.name}`);
            this.loadTeams();
            this.loadGames();

            const { onSwapCompleted } = this.props;
            if (onSwapCompleted) {
              onSwapCompleted();
            }
          }
        )
        .catch(onError)
        .finally(
          () => this.setState({ swapping: null })
        )
    });
  }

  renderFooter() {
    const { onClose } = this.props;
    const { teamOne, teamTwo, swapping } = this.state;

    return (
      <div>
        <Button text="Close" onClick={onClose} className="mg-right-sm"/>
        <Button type="primary" text="Swap" onClick={() => this.swapTeams()}
          disabled={!teamOne || !teamTwo || swapping}/>
      </div>
    );
  }

  render() {
    const { open, title, onClose } = this.props;

    return (
      <Modal size="lg" open={open} onClose={onClose}>
        <Modal.Title onClose={onClose}>{title || 'Swap Teams'}</Modal.Title>
        <Modal.Body>{this.renderBody()}</Modal.Body>
        <Modal.Footer>{this.renderFooter()}</Modal.Footer>
      </Modal>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/components/modals/SwapTeamsModal.jsx